import axios, { AxiosError } from "axios";
import { useContext } from "react";
import useSWR from "swr";
import { pollingInterval } from "../../../constants/pollingInterval";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { IAevoPosition, ICollateral } from "../../../interfaces/aevo";
import { ADMIN_API_URL } from "../../../utils/env";

export interface ISubaccount {
  subaccount: string;
  name: string;
  archived: boolean;
  balances: ICollateral[];
  positions: IAevoPosition[];
}

export interface ISubaccounts {
  account: string;
  // Only set when `account` is itself a subaccount, in which case `subaccounts` is empty
  parent?: string;
  max_subaccounts?: number;
  subaccounts: ISubaccount[];
}

interface IUseSubaccountsOptions {
  // Dashboard rows look up once; the account page keeps polling
  poll?: boolean;
}

export const useSubaccounts = (account?: string, { poll = true }: IUseSubaccountsOptions = {}) => {
  const { tokenId, queryConfig } = useContext(AuthContext);
  const endpoint = ADMIN_API_URL(AdminEndpointEnum.SUBACCOUNTS);

  const swr = useSWR<ISubaccounts, AxiosError>(
    tokenId && account ? [endpoint, tokenId, account.toLowerCase()] : null,
    async () => {
      const response = await axios.get<ISubaccounts>(endpoint, {
        ...queryConfig(),
        params: { account },
      });
      return response.data;
    },
    {
      refreshInterval: poll ? pollingInterval[AdminEndpointEnum.SUBACCOUNTS] : 0,
      revalidateOnFocus: poll,
      // The endpoint needs READ_SYSTEM; without it the indicators just stay hidden
      shouldRetryOnError: false,
    }
  );

  const parent = swr.data?.parent || undefined;
  const subaccounts = swr.data?.subaccounts ?? [];

  return {
    ...swr,
    parent,
    subaccounts,
    isSubaccount: Boolean(parent),
    isMaster: subaccounts.length > 0,
  };
};

/**
 * The master/subaccount family an account belongs to. On a subaccount this also loads the master's
 * list, so siblings (and the subaccount's own name) are known. Empty for standalone accounts.
 */
export const useAccountFamily = (account?: string) => {
  const own = useSubaccounts(account);
  const master = own.parent ?? (own.isMaster ? own.data?.account : undefined);
  const { subaccounts } = useSubaccounts(own.parent);
  const members = own.isSubaccount ? subaccounts : own.subaccounts;

  return {
    master,
    subaccounts: members,
    isMaster: own.isMaster,
    isSubaccount: own.isSubaccount,
    hasFamily: Boolean(master),
  };
};
